import { isNonEmptyString, reduceErrors, validate, validateAll, ValidationErrors } from "@util/validations"
import { NonRefundableAdjustment, totalNonRefundable, validateAdjustment } from "./NonRefundableAdjustment"

export interface Cancellation {
  maxRefundable: number
  maxStripeRefundable: number
  netPaid: number
  totalConvenienceFeesFromPayments: number
  totalAffirmFeesFromPayments: number
  totalAchDiscounts: number
  feesAreNonRefundable: boolean
  refundMethod: "stripe" | "manual"
  manualReference: string
  agreeCancellationIsIrreversible: boolean
  nonRefundableAdjustments: NonRefundableAdjustment[]
}

export function cancellationTotal(cancellation: Cancellation): number {
  return (
    cancellation.maxRefundable + cancellation.totalConvenienceFeesFromPayments + cancellation.totalAffirmFeesFromPayments
  )
}

export function currentNonRefundableFees(cancellation: Cancellation): number {
  return cancellation.feesAreNonRefundable
    ? cancellation.totalConvenienceFeesFromPayments + cancellation.totalAffirmFeesFromPayments
    : 0
}

export function toBeRefunded(cancellation: Cancellation, insuranceOnly: boolean): number {
  let refundableNet = cancellation.maxRefundable - totalNonRefundable(cancellation.nonRefundableAdjustments, insuranceOnly)
  let totalFees = cancellation.totalConvenienceFeesFromPayments + cancellation.totalAffirmFeesFromPayments

  let refundableFees = totalFees - currentNonRefundableFees(cancellation)
  return Math.max(refundableNet + refundableFees, 0)
}

export function canBeRefundedUsingStripe(cancellation: Cancellation, insuranceOnly: boolean): boolean {
  if (cancellation.feesAreNonRefundable) {
    return toBeRefunded(cancellation, insuranceOnly) <= cancellation.maxStripeRefundable
  } else {
    return (
      toBeRefunded(cancellation, insuranceOnly) <=
      cancellation.maxStripeRefundable +
        cancellation.totalConvenienceFeesFromPayments +
        cancellation.totalAffirmFeesFromPayments
    )
  }
}

export function notToBeRefunded(cancellation: Cancellation, insuranceOnly: boolean): number {
  return totalNonRefundable(cancellation.nonRefundableAdjustments, insuranceOnly) + currentNonRefundableFees(cancellation)
}

export function validateCancellation(data: Cancellation): ValidationErrors<Cancellation> {
  return reduceErrors({
    agreeCancellationIsIrreversible: [
      validate(
        data.agreeCancellationIsIrreversible,
        "You must acknowledge that cancellations are immediate and irreversible",
      ),
    ],
    manualReference: [
      data.refundMethod === "manual" &&
        validate(isNonEmptyString(data.manualReference), "You must provide a reference for manual refunds"),
    ],
    nonRefundableAdjustments: [
      validateAll(
        data.nonRefundableAdjustments,
        validateAdjustment,
        "All non-refundable adjustments must be positive numbers",
      ),
    ],
  })
}
